import { border, color } from "@chakra-ui/react";

/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
export default {
    baseStyle: (props) => ({
        swatchGroup: {
            flexDirection: 'column',
            display: 'grid'
        },
        swatchLabel: {
            marginBottom: 3,
            ...(props.isPDP && {
                color: 'black',
                textStyle: 'bodyXsRegular',
                marginBottom: 2
            }),
            ...(props.displayLabelOnly && {
                fontSize: 'sm',
                fontWeight: 'bold'
            })
        },
        swatchLabelInner: {
            ...(props.isGiftPage && {textStyle: 'bodyXsRegular'})
        },
        swatchDisplayName: {
            color: 'black',
            textStyle: 'bodyXsRegular',
            ...(props.isPDP && {
                minH: '16px'
            }),
        },
        swatch: {
            position: 'relative',
            backgroundColor: 'white',
            _focus: {
                outline: 'none',
                boxShadow: 'outline'
            }
        },
        swatchesWrapper: {
            flexWrap: 'wrap',
            ...(props.isSizeSwatch &&
                props.isPDP && {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 1
                }),
            ...((props.giftCardType || props.giftCardValue || props.giftECard) && {
                display: 'grid',
                gap: '4px',
                gridTemplateColumns:
                    props.giftCardValue || props.giftECard
                        ? `repeat(${props.childrenCount || 5}, 1fr)`
                        : props.giftCardType
                        ? `repeat(${props.childrenCount || 4}, 1fr)`
                        : 'repeat(2, 1fr)'
            }),
            ...(props?.id === 'color' &&
                !props.giftCardType && {
                    columnGap: {base: 0, lg: 1},
                    rowGap: {base: 1},
                    marginX: {base: '-8px', lg: 0}
                }),
        },
        swatchButton: {
            border: '1px solid',
            borderColor: 'webTheme.strokeTertiary',
            _disabled: {
                opacity: 1
            },
        }
    }),

    variants: {
        circle: (props) => ({
            swatch: {
                position: 'relative',
                width: props.isProductTile ? '16px' : '24px',
                height: props.isProductTile ? '16px' : '24px',
                ...(props.isQuickView ||
                    props.isPDP && {
                        width: {base: '24px', lg: '16px'},
                        height: {base: '24px', lg: '16px'}
                }),
                overflow: 'hidden',
                borderRadius: '100%',
                border: '1px solid',
                padding: 0,
                minWidth: 'auto',
                minHeight: 'auto',
                borderColor: 'webTheme.strokeTertiary',
                ...(props.selected && {
                    borderColor: 'black'
                }),
                _before: {
                    content: '""',
                    display: props.disabled ? 'block' : 'none',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '1px',
                    height: '140%',
                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                    backgroundColor: 'webTheme.strokeTertiary',
                    zIndex: 1
                },
                _hover: {
                    backgroundColor: 'none'
                }
            },
            swatchButton: {
                border: 0,
                width: '100%',
                height: '100%',
                ...(props.selected && {
                    border: '2px solid',
                    borderColor: 'white',
                    borderRadius: '100%',
                    overflow: 'hidden'
                }),
            }
        }),
        square: (props) => ({
            swatch: {
                border: '1px solid',
                padding: 0,
                minWidth: {base: '53px', lg: '65px'},
                minHeight: '40px',
                borderColor: 'webTheme.strokeTertiary',
                ...(props.selected && {
                    borderColor: 'webTheme.backgroundReverse',
                    backgroundColor: 'webTheme.backgroundReverse',
                }),
                ...(props.isQuickAdd && {
                    minWidth: {base: '54px', lg: '54px'},
                    ...(props.disabled && {
                        backgroundColor: 'webTheme.fieldDisabled'
                    }),
                }),
                ...(props.isPDP && {
                    minWidth: {base: '54px', lg: '69px'},
                    ...(props.disabled && {
                        backgroundColor: 'webTheme.fieldDisabled'
                    }),
                }),
                ...(props.isFindInStore && {
                    minWidth: {base: '54px', lg: '54px'},
                    ...(props.disabled && {
                        backgroundColor: 'webTheme.fieldDisabled'
                    }),
                }),
                ...(props.disabled && {
                    backgroundColor: 'webTheme.strokeTertiary',
                    ...(props.selected && {
                        backgroundColor: 'webTheme.backgroundReverse'
                    })
                }),
                ...(props.isNotifyMe && {
                    backgroundColor: 'webTheme.fieldDisabled',
                    borderColor: 'webTheme.strokeTertiary',
                    ...(props.selected && {
                        backgroundColor: 'webTheme.backgroundReverse',
                        borderColor: 'webTheme.strokeDefault',
                    })
                }),
                _hover: {
                    backgroundColor: 'none',
                    borderColor: 'black',
                },
                _active: {
                    borderColor: 'black',
                    ...(props.selected && {
                        backgroundColor: 'webTheme.backgroundReverse'
                    }),
                    ...(!props.selected && {
                        backgroundColor: props.disabled
                            ? 'webTheme.strokeTertiary'
                            : 'white'
                    })
                }
            },
            swatchButton: {
                border: 0,
                textStyle: 'bodyXsRegular',
                textTransform: 'none',
                color: 'webTypo.textPrimary',
                ...(props.selected && {
                    color: 'webTypo.textOnColor',
                }),
                ...(props.disabled && {
                    color: 'webTypo.textDisabled',
                    ...(props.selected && {
                        color: 'webTypo.textDisabled',
                    })
                }),
                ...(props.isNotifyMe && {
                    color: 'webTypo.textPrimary',
                    ...(props.selected && {
                        color: 'webTypo.textOnColor',
                    })
                })
            }
        }),
        squareRefinements: () => ({
            swatch: {
                border: '1px solid',
                padding: 0,
                minWidth: {base: '53px'},
                minHeight: '40px',
                borderColor: 'webTheme.strokeTertiary',
                _hover: {
                    backgroundColor: 'none',
                    borderColor: 'webTheme.backgroundReverse'
                }
            },
            swatchButton: {
                border: 0,
                textStyle: 'bodyXsRegular',
                textTransform: 'none',
                color: 'webTypo.textPrimary'
            },
            groupTitle: {
                textStyle: 'bodyXsRegular',
                color: 'webTypo.textSecondary',
                mb: 3
            }
        }),
        giftCard: (props) => ({
            swatch: {
                borderRadius: 5,
                overflow: 'hidden',
                aspectRatio: '87.23 / 54.91',
                borderColor: 'gray.900',
                border: props.selected ? '1.5px solid' : 0,
                padding: `${props.selected ? '2px' : '0'} !important`,
                height: 'auto',
                width: '100%',
                // Reset line-height and flex-direction from outline button variant
                lineHeight: 'normal !important',
                flexDirection: 'row !important',
                paddingTop: `${props.selected ? '2px' : '0'} !important`,
                paddingBottom: `${props.selected ? '2px' : '0'} !important`
            },
            swatchButton: {
                height: '100%',
                width: '100%',
                minWidth: 'unset'
            }
        })
    }
}
